import React, { useState, useEffect } from "react";
import { analyticsService } from "../services/analytics.service";
import "./PrivacyConsent.css";

interface PrivacyConsentProps {
  onConsentChange?: (consent: boolean) => void;
}

const PrivacyConsent: React.FC<PrivacyConsentProps> = ({ onConsentChange }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showExpandedPreferences, setShowExpandedPreferences] = useState(false);
  const [consentGiven, setConsentGiven] = useState<boolean | null>(null);

  useEffect(() => {
    // Check if user has already given consent
    const savedConsent = localStorage.getItem("orindra_analytics_consent");
    if (savedConsent === null) {
      // Show banner if no consent decision has been made
      setShowBanner(true);
    } else {
      const consent = savedConsent === "true";
      setConsentGiven(consent);
      analyticsService.setConsent(consent);
      onConsentChange?.(consent);
    }
  }, [onConsentChange]);

  const handleConsent = (consent: boolean) => {
    setConsentGiven(consent);
    setShowBanner(false);

    // Save consent to localStorage
    localStorage.setItem("orindra_analytics_consent", consent.toString());

    // Update analytics service
    analyticsService.setConsent(consent);

    // Notify parent component
    onConsentChange?.(consent);

    // Track consent event
    if (consent) {
      analyticsService.trackEvent("privacy_consent_given", {
        event_category: "privacy",
        event_label: "analytics_enabled",
      });
    } else {
      analyticsService.trackEvent("privacy_consent_denied", {
        event_category: "privacy",
        event_label: "analytics_disabled",
      });
    }
  };

  const handleManagePreferences = () => {
    // Show expanded preferences
    setShowExpandedPreferences(true);
    analyticsService.trackEvent("privacy_preferences_opened", {
      event_category: "privacy",
      event_label: "preferences_modal_opened",
    });
  };

  const handleGranularConsent = (analytics: boolean, marketing: boolean) => {
    // For now, we only have analytics, but this structure allows for future expansion
    const consent = analytics; // Marketing is always false for now
    handleConsent(consent);
    setShowExpandedPreferences(false);

    analyticsService.trackEvent("granular_consent_given", {
      event_category: "privacy",
      event_label: `analytics_${analytics}_marketing_${marketing}`,
    });
  };

  const handleBackToMain = () => {
    setShowExpandedPreferences(false);
  };

  if (!showBanner) {
    return null;
  }

  // Show expanded preferences if requested
  if (showExpandedPreferences) {
    return (
      <div className="privacy-consent-banner">
        <div className="privacy-consent-content">
          <div className="privacy-consent-text">
            <h3>🔒 Privacy Preferences</h3>
            <p>
              Choose which types of cookies and tracking you'd like to allow:
            </p>

            <div className="privacy-consent-options">
              <div className="consent-option">
                <label className="consent-option-label">
                  <input
                    type="checkbox"
                    className="consent-checkbox"
                    defaultChecked={true}
                    disabled
                  />
                  <span className="consent-option-text">
                    <strong>Essential Cookies</strong>
                    <br />
                    <small>Required for the website to function properly</small>
                  </span>
                </label>
              </div>

              <div className="consent-option">
                <label className="consent-option-label">
                  <input
                    type="checkbox"
                    className="consent-checkbox"
                    id="analytics-consent"
                    defaultChecked={consentGiven === true}
                  />
                  <span className="consent-option-text">
                    <strong>Analytics Cookies</strong>
                    <br />
                    <small>
                      Help us understand how visitors use our website
                    </small>
                  </span>
                </label>
              </div>

              <div className="consent-option">
                <label className="consent-option-label">
                  <input
                    type="checkbox"
                    className="consent-checkbox"
                    disabled
                    defaultChecked={false}
                  />
                  <span className="consent-option-text">
                    <strong>Marketing Cookies</strong>
                    <br />
                    <small>
                      Used to deliver personalized advertisements (not available
                      yet)
                    </small>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="privacy-consent-actions">
            <button
              className="privacy-consent-btn privacy-consent-secondary"
              onClick={handleBackToMain}
            >
              Back
            </button>
            <button
              className="privacy-consent-btn privacy-consent-accept"
              onClick={() => {
                const analyticsConsent =
                  (
                    document.getElementById(
                      "analytics-consent"
                    ) as HTMLInputElement
                  )?.checked || false;
                handleGranularConsent(analyticsConsent, false);
              }}
            >
              Save Preferences
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show main consent banner
  return (
    <div className="privacy-consent-banner">
      <div className="privacy-consent-content">
        <div className="privacy-consent-text">
          <h3>🍪 We value your privacy</h3>
          <p>
            We use cookies and analytics to improve your experience and
            understand how you use our site. This helps us create better
            products for you.
          </p>
          <div className="privacy-consent-links">
            <a
              href="/privacy"
              onClick={() =>
                analyticsService.trackEvent("privacy_link_clicked", {
                  event_category: "privacy",
                  event_label: "privacy_policy_link",
                })
              }
            >
              Privacy Policy
            </a>
            <span> • </span>
            <button
              className="privacy-preferences-link"
              onClick={handleManagePreferences}
            >
              Manage Preferences
            </button>
          </div>
        </div>

        <div className="privacy-consent-actions">
          <button
            className="privacy-consent-btn privacy-consent-accept"
            onClick={() => handleConsent(true)}
          >
            Accept All
          </button>
          <button
            className="privacy-consent-btn privacy-consent-decline"
            onClick={() => handleConsent(false)}
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyConsent;
